import type { ReviewVerdict } from "./reviewReport";

export const REVIEW_OUTCOME_LABELS = {
  approved: "Approved",
  comment: "Comments",
  changes_requested: "Changes requested",
} as const;

export const REVIEW_OUTCOME_CLASSES = {
  approved: "text-ds-green",
  comment: "text-neutral-700 dark:text-neutral-300",
  changes_requested: "text-amber-600 dark:text-amber-400",
} as const;

export const REVIEW_VERDICT_LABELS = {
  approve: "Approve",
  not_yet: "Not yet",
} as const;

export const REVIEW_VERDICT_CLASSES = {
  approve: REVIEW_OUTCOME_CLASSES.approved,
  not_yet: REVIEW_OUTCOME_CLASSES.changes_requested,
} as const;

export const REVIEW_DISPOSITION_LABELS = {
  fix: "Fix",
  wont_fix: "Won't fix",
  fast_follow: "Fast follow",
} as const;

const APPROVAL_DECISION_LABELS = new Map([
  ["approved", "Approved on GitHub"],
  ["would_approve", "Would approve (shadow)"],
  ["approving", "Approval pending"],
  ["unconfirmed", "Approval pending"],
  ["refused", "Not approved"],
  ["withdrawing", "Approval withdrawn"],
  ["withdrawn", "Approval withdrawn"],
  ["would_withdraw", "Would withdraw (shadow)"],
]);

const APPROVAL_SKIP_REASONS = new Map([
  ["verdict_not_yet", "the lead agent said not yet"],
  ["risk_missing", "risk was not assessed"],
  ["risk_not_low", "risk is not low"],
  ["auto_approve_off", "auto-approve is off"],
  ["findings_not_posted", "the findings were never posted"],
  ["proof_issues", "Proof found issues"],
  ["head_moved", "new commits were pushed"],
  ["base_changed", "the base branch changed"],
  ["merge_base_changed", "the base branch was rewritten"],
  ["reviewed_base_missing", "the reviewed base is unknown"],
  ["draft", "the PR is a draft"],
  ["closed", "the PR is closed"],
  ["fork", "the PR is from a fork"],
  ["bot_author", "a bot opened the PR"],
  ["already_approved", "this commit is already approved"],
  ["github_unavailable", "GitHub was unavailable"],
]);

/** e.g. `skipped` + `risk_not_low` → `"Not approved: risk is not low"`; null when there is nothing to say. */
export function reviewVerdictApprovalState(
  verdict: Pick<
    ReviewVerdict,
    "verdict" | "approval_decision" | "approval_skip_reason"
  >,
): string | null {
  const { approval_decision: decision, approval_skip_reason: skipReason } =
    verdict;
  if (decision === "skipped" && skipReason === "proof_running") {
    return "Approval waiting for Proof";
  }
  if (decision === "skipped") {
    const reason = skipReason
      ? APPROVAL_SKIP_REASONS.get(skipReason) ?? skipReason.replaceAll("_", " ")
      : null;
    return reason ? `Not approved: ${reason}` : "Not approved";
  }
  if (decision === null) {
    return verdict.verdict === "approve" ? "Approval pending" : null;
  }
  return APPROVAL_DECISION_LABELS.get(decision) ?? null;
}

// Place it between whitespace-nowrap segments, never inside one: the NBSP
// keeps the middot with the segment before it, and the plain space after it
// is the line's only wrap point.
export const REVIEW_DETAIL_SEPARATOR = "\u00a0· ";

/**
 * Status-pill dots per DESIGN.md: the chip stays neutral, the dot carries the
 * outcome. These are review outcomes, not run statuses: `STATUS_COLORS.success`
 * is blue and would read the same as the indigo comment dot.
 */
export const REVIEW_OUTCOME_DOT_CLASSES = {
  approved: "bg-[var(--chart-12)]",
  comment: "bg-[var(--chart-9)]",
  changes_requested: "bg-[var(--chart-3)]",
} as const;

export const REVIEW_VERDICT_DOT_CLASSES = {
  approve: REVIEW_OUTCOME_DOT_CLASSES.approved,
  not_yet: REVIEW_OUTCOME_DOT_CLASSES.changes_requested,
} as const;
